/**
 * Підняття бази й контекст для use-case'ів.
 *
 * База живе у воркері (OPFS дає `FileSystemSyncAccessHandle` лише там),
 * тож старт асинхронний і може провалитися. Три стани показуються явно,
 * а не ховаються за спінером назавжди: користувач має розуміти, чи його
 * записи зараз узагалі є куди зберігати.
 */
import type {
  AccountKeys,
  CipherPort,
  ContentCipherPort,
  Ctx,
  LinkPort,
  PeopleCtx,
  PersonRepo,
} from "@beresta/core";
import type { SearchPort } from "@beresta/core";
import type { OutboxOptions, Profile, ToPayload } from "@beresta/db";

/**
 * Сховище профілю так, як його бачить екран.
 *
 * Свій тип, а не сам клас: провайдер віддає його **ліниво**, тож на місці
 * виклику має стояти інтерфейс, а не обіцянка конкретної реалізації.
 */
export interface ProfileRepo {
  load(): Promise<Profile>;
  save(profile: Profile): Promise<void>;
}
import {
  OutboxEntryRepo,
  OutboxHorizonRepo,
  SqlEntryRepo,
  SqlHorizonRepo,
  SqlOutbox,
  SqlSearch,
  SqlSyncState,
  WebDb,
  countHorizonsToRemap,
  countPending,
  formatWatermark,
  loadClockState,
  reencryptContent,
  remapHorizonIds,
} from "@beresta/db";
import {
  SYNC_KEYS,
  type DataKey,
  type HorizonKey,
  type HorizonKind,
  type HorizonNamespace,
  type EntryRepo,
  type HorizonRepo,
  type IdPort,
  type OutboxPort,
  type SyncStatePort,
  deriveHorizonNamespace,
  horizonIdFor,
} from "@beresta/core";
// `?worker&url` дає адресу зібраного воркера замість конструктора: нам
// потрібно дописати до неї параметри (нижче), а конструктор від Vite такої
// змоги не лишає. Через `new URL(...)` з іменем пакета це не працює зовсім —
// у `new URL` не резолвляться голі специфікатори.
import dbWorkerUrl from "@beresta/db/worker?worker&url";
// `?url` віддає адресу файлу після збірки. Обгортка sqlite3 інакше шукає
// wasm поруч зі своїм JS і промахується — dev-сервер віддає index.html,
// а WebAssembly падає на «expected magic word».
import wasmUrl from "@aztec/sqlite3mc-wasm/vendor/jswasm/sqlite3.wasm?url";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type LocalAccount,
  forgetKeys,
  forgetSyncToken,
  loadAccount,
  loadPendingRekey,
  loadSyncToken,
  recallKeys,
} from "../platform/account.js";
import type { SyncEngine } from "../sync/engine.js";
import { lockDueAtStart, watchAutoLock } from "../platform/autolock.js";
import { createCipher } from "../platform/cipher.js";
import { createContentCipher } from "../platform/contentCipher.js";
import { claimStorage, watchClaims } from "./tabs.js";

import { type AppIds, createClock, createIds } from "../platform/ports.js";

const DB_NAME = "beresta";

/**
 * Обгортка sqlite3 при старті намагається встановити всі варіанти OPFS VFS
 * і про невдачу пише в консоль. Нам потрібен лише `opfs-sahpool`; два інші
 * тягнуть власний підворкер, якого немає у збірці, — і кожен старт лишав
 * дві помилки в консолі.
 *
 * Самі по собі вони нешкідливі, але шум маскує справжні поломки сховища,
 * а їх тут уже було три. Прапорець `<vfs>-disable` у параметрах адреси
 * воркера змушує пропустити встановлення, а не пробувати й не змогти.
 */
const SKIP_UNUSED_VFS = "opfs-disable&opfs-wl-disable";

/**
 * Скільки чекати на підняття сховища, перш ніж визнати його непіднятим.
 *
 * Кожен крок має власний таймаут: `open` десять секунд, `migrate` дві
 * хвилини. Але між кроками є місця без жодного — підняття воркера,
 * `indexedDB.open` для ключа пристрою, — і застосунок там може висіти
 * скільки завгодно, показуючи «Відкриваю сховище…».
 *
 * Так уже було: у прогоні перевірок екран лишався в цьому стані понад
 * тридцять секунд. Порожній екран із багатокрапкою не каже користувачеві
 * нічого; чесна відмова каже, що робити далі.
 *
 * Сорок секунд — верхня межа над таймаутом самого `open` (тридцять). Так
 * повідомлення лишається конкретним: спершу відмовить крок, який завис,
 * і скаже, який саме, а спільний дедлайн ловить лише те, у чого власного
 * таймауту немає — підняття воркера й `indexedDB.open` для ключа пристрою.
 */
const STARTUP_DEADLINE_MS = 40_000;

/**
 * Стан акаунта на цьому пристрої.
 *
 * `none` — онбординг ще попереду. `locked` — акаунт є, але сейф пристрою
 * порожній: або це новий пристрій, або тут натиснули «вийти». Оскільки
 * відкрита вкладка не блокується ([ADR-0019]), у `locked` можна потрапити
 * лише цими двома шляхами, а не за таймером.
 */
export type AccountState =
  | { readonly status: "none" }
  | { readonly status: "locked"; readonly email: string }
  | {
      readonly status: "unlocked";
      readonly email: string;
      readonly keys: AccountKeys;
    };

export type DbState =
  | { readonly status: "starting" }
  /**
   * Сховище віддане іншій вкладці. Не помилка й не втрата: архів цілий,
   * просто ним зараз користуються в іншому місці.
   */
  | { readonly status: "parked"; readonly takeOver: () => void }
  | {
      readonly status: "ready";
      readonly ctx: Ctx & PeopleCtx;
      readonly db: WebDb;
      readonly account: AccountState;
      /** Сейф пристрою. Потрібен тим, хто заводить або відмикає акаунт. */
      readonly device: CipherPort;
      /** Той самий шифр, що в репозиторіях. Потрібен перешифруванню архіву. */
      readonly content: ContentCipherPort;
      /** Перечитує акаунт зі сховища — після онбордингу, входу чи виходу. */
      readonly refreshAccount: () => Promise<void>;
      /**
       * Той самий пошук, що й у `ctx`. Продубльований тут лише заради двох
       * речей, яким `Ctx` не місце: перебудови індексу з налаштувань і шва
       * для перевірок продуктивності.
       *
       * Індексувати звідси не треба — це робить сам use-case.
       */
      readonly search: SearchPort;
      /**
       * Профіль власника — [ADR-0037].
       *
       * За лінивою межею разом із самим сховищем: перший кадр показує
       * день, а не візитку, і клас профілю в ньому нічого не робить.
       */
      readonly profile: ProfileRepo;
      /**
       * Рушій надсилання — за запитом, а не значенням.
       *
       * Він тягне за собою транспорт, заливку й кодек дельт, а потрібен
       * лише тим, хто справді синхронізується. У холодному старті це
       * два кілобайти після gzip, тобто рівно та різниця, через яку
       * бюджет `check:bundle` перестає сходитися.
       */
      readonly syncEngine: () => Promise<SyncEngine>;
      /** Стан синхронізації в `sync_state` — потрібен вмиканню. */
      readonly syncState: SyncStatePort;
    }
  | { readonly status: "failed"; readonly reason: string };

/**
 * Контекст експортований навмисно — заради рівня компонентних перевірок.
 *
 * Без цього єдиний спосіб намалювати екран, який ходить у базу, — підняти
 * браузер: `DbProvider` тягне за собою воркер, OPFS і шифр. Тобто перевірка
 * порожнього стану коштувала б стільки ж, скільки перевірка синхронізації
 * між двома вкладками, і рівні переставали б відрізнятися.
 *
 * Підставляється сюди **справжній** `Ctx` над справжньою SQLite у памʼяті
 * (`src/test/dbHarness.tsx`), а не підробка: інакше перевірка перестала б
 * падати з правильної причини.
 */
export const DbContext = createContext<DbState>({ status: "starting" });

export function useDb(): DbState {
  return useContext(DbContext);
}

/**
 * Готовий контекст або виняток. Для місць, які взагалі не мають сенсу
 * без бази, — щоб не тягнути перевірку статусу крізь кожен обробник.
 */
export function useCtx(): Ctx & PeopleCtx {
  const state = useDb();
  if (state.status !== "ready") {
    throw new Error("useCtx викликано до готовності бази");
  }
  return state.ctx;
}

/**
 * Сховище піднімається рівно один раз на вкладку.
 *
 * OPFS SAHPool захоплює файли ексклюзивно: другий воркер із тим самим імʼям
 * пулу дістає `NoModificationAllowedError` і не піднімається, а разом із ним
 * тихо провалюються всі подальші записи. У розробці це трапляється щоразу —
 * `StrictMode` навмисно монтує ефекти двічі, — тож без цього синглтона
 * застосунок не зберігає нічого, і виглядає це як «редактор не працює».
 *
 * Обіцянка тут саме «один раз на вкладку»: між вкладками SAHPool не
 * ділиться взагалі, і це окрема невирішена задача.
 */
let started: Promise<Started> | null = null;

function startOnce(): Promise<Started> {
  started ??= start();
  return started;
}

/** Сховище віддане іншій вкладці; чекаємо прямої дії людини. */
let parked = false;
let notifyParked: (() => void) | null = null;

/**
 * Відпустити сховище на вимогу іншої вкладки.
 *
 * Саме **відпустити**, а не просто показати повідомлення: `opfs-sahpool`
 * тримає файли ексклюзивно, і доки цей воркер живий, сусідня вкладка
 * не підніметься.
 */
async function releaseStorage(): Promise<void> {
  if (!started) return;
  const ready = await started.catch(() => null);
  started = null;
  startedRef = null;
  engine?.stop();
  engine = null;
  parked = true;
  await ready?.db.close().catch(() => undefined);
  notifyParked?.();
}

/**
 * Забрати сховище назад — лише за прямою дією людини.
 *
 * Автоматичного перезабирання немає навмисно: дві вкладки, які
 * перезабирають одна в одної, дали б нескінченний пінг-понг, і кожна
 * виглядала б зламаною.
 */
function takeOverStorage(): void {
  parked = false;
  claimStorage();
}

watchClaims(() => {
  void releaseStorage();
});

/** Відмова, якщо крок не вклався у відведений час. Без цього — вічне очікування. */
async function within<T>(
  what: string,
  ms: number,
  work: Promise<T>,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${what} не вклалося у ${ms / 1000} с`)),
          ms,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function wipePending(): boolean {
  try {
    return localStorage.getItem("beresta:wipe") === "1";
  } catch {
    return false;
  }
}

async function start(): Promise<Started> {
  // D1 «Видалити архів» доробляється тут, до відкриття сховища: поки база
  // відкрита, її файли захоплені воркером і стерти їх не можна. Позначка
  // читається тут напряму, а модуль довантажується лише тоді, коли вона
  // стоїть: зайвий запит за чанком на кожному старті — це затримка
  // першого кадру заради події, що буває раз у житті архіву.
  if (wipePending()) await (await import("../platform/wipe.js")).wipeNow();
  return within("підняття сховища", STARTUP_DEADLINE_MS, startInner());
}

/**
 * Зберігає стан годинника, коли вкладка йде у фон або закривається.
 *
 * **Не за таймером.** Спершу так і було — запис раз на секунду після
 * будь-якої мітки, — і це додало фоновий запис у базу рівно у вікно,
 * де перевірка шифрування читає файли OPFS. Запис у базу «про всяк
 * випадок» посеред роботи не безкоштовний: файли захоплені, і кожен
 * зайвий запис — це ще одна мить, коли їх не можна прочитати.
 *
 * Момент виходу правильний і по суті. Водяний знак потрібен рівно для
 * одного випадку — годинник пристрою пішов назад **між запусками**, —
 * тож достатньо зберегти його перед тим, як запуск скінчиться. Аварійне
 * закриття лишить знак несвіжим, і це та сама поведінка, що була досі.
 */
function keepWatermark(ids: AppIds, state: SyncStatePort): void {
  const flush = () => {
    void state
      .set(SYNC_KEYS.hlcWatermark, formatWatermark(ids.state()))
      .catch(() => undefined);
  };
  // `visibilitychange` на приховуванні, а не `beforeunload`: на мобільних
  // вкладку прибивають без жодного `unload`, і це єдина подія, яка
  // справді приходить.
  globalThis.addEventListener?.("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  globalThis.addEventListener?.("pagehide", flush);
}

/**
 * Ключ даних живе тут, поки відкрита вкладка, і більше ніде.
 *
 * Модульна змінна, а не стан React: шифр вмісту читає її з середини
 * репозиторіїв, які створюються один раз при старті. Через `useState`
 * цього не передати, не перебудовуючи весь контекст.
 */
let dataKeys: AccountKeys | null = null;

/**
 * Простір імен горизонтів, порахований один раз на ключ даних.
 *
 * HKDF на кожне створення горизонта був би дрібницею, але горизонт
 * створюється при кожному відкритті дня — і дрібниця в гарячому шляху
 * перестає бути дрібницею.
 */
let horizonNs: { readonly dek: DataKey; readonly ns: HorizonNamespace } | null =
  null;

/**
 * Ідентифікатор горизонта: детермінований, коли ключ даних є, і
 * випадковий, коли його ще немає ([ADR-0023]).
 *
 * Випадковий — це не запасний варіант, а справжній стан до онбордингу.
 * Такі горизонти потім переписує `remapHorizonIds`, і саме тому вона
 * запускається двічі: при старті й одразу після появи акаунта.
 */
export function horizonIdWith(
  ids: IdPort,
): (kind: HorizonKind, key: HorizonKey) => string {
  return (kind, key) => {
    const dek = dataKeys?.dek;
    if (!dek) return ids.next();
    if (horizonNs?.dek !== dek)
      horizonNs = { dek, ns: deriveHorizonNamespace(dek) };
    return horizonIdFor(horizonNs.ns, kind, key);
  };
}

/**
 * Міграції, які не можна виконати без ключа даних, і які тому не
 * входять у звичайні міграції схеми.
 *
 * Обидві безпечні до повторного запуску й до обриву посеред роботи —
 * тому їх можна кликати щоразу, не питаючи, чи вони вже відпрацювали.
 */
export async function catchUpMigrations(
  started: Pick<Started, "db" | "ctx" | "content">,
): Promise<void> {
  if (!dataKeys) return;
  const horizonId = horizonIdWith(started.ctx.ids);
  if ((await countHorizonsToRemap(started.db, horizonId)) > 0) {
    await remapHorizonIds(started.db, horizonId);
  }
  if ((await countPending(started.db)) > 0) {
    await reencryptContent(started.db, started.content);
  }
}

/**
 * Підняти сховище, наполягаючи.
 *
 * Оголошуємо себе власником — інші вкладки відпускають, — але звільнення
 * не миттєве: воркер там ще має завершитися. Тому кілька спроб із
 * паузою. Без них новій вкладці довелося б оновлювати сторінку руками
 * рівно тоді, коли все вже майже готове.
 */
async function openStorage(): Promise<WebDb> {
  claimStorage();
  let last: unknown;
  for (let attempt = 0; attempt < 6; attempt++) {
    const worker = new Worker(`${dbWorkerUrl}?${SKIP_UNUSED_VFS}`, {
      type: "module",
    });
    const db = new WebDb(worker);
    try {
      await db.open(DB_NAME, wasmUrl);
      return db;
    } catch (cause) {
      last = cause;
      await db.close().catch(() => undefined);
      if (!WebDb.busy(cause instanceof Error ? cause.message : String(cause)))
        throw cause;
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  }
  throw last;
}

async function startInner(): Promise<Started> {
  const db = await openStorage();
  await db.migrate();

  // Годинник піднімається з того місця, де спинився. Стан лежить
  // у `sync_state`, тож читати його можна лише після міграції.
  const syncState = new SqlSyncState(db);
  // Не лише зі знака: архів повний міток, старших за нього — усіх,
  // написаних до того, як цей код існував. `loadClockState` бере
  // максимум зі знака й найбільшої мітки в базі.
  const ids = createIds(undefined, await loadClockState(db, syncState));
  keepWatermark(ids, syncState);
  const clock = createClock();
  // Шифр піднімається після міграції: без бази він нікому не потрібен,
  // а помилка ключа не має виглядати як помилка схеми.
  const device = await createCipher();
  // Шифр вмісту читає ключ даних щоразу, бо той зʼявляється вже після
  // старту — коли людина пройшла онбординг або ввела фразу.
  const cipher = createContentCipher(device, () => dataKeys?.dek ?? null);
  // Витяг тексту передається ззовні з тієї ж причини, що й у пошуку взагалі:
  // тіло запису — це CRDT, і його розбір живе там, де є Yjs. Імпорт ліниво,
  // бо перебудова індексу трапляється рідко, а Yjs важить як пів застосунку.
  const search = new SqlSearch(db, async (body) => {
    const { renderBody } = await import("../screens/renderBody.js");
    return renderBody(body);
  });
  // Ключ даних дістається до першого малювання, якщо сейф пристрою його
  // памʼятає. Інакше екран блимнув би замком і одразу зник.
  const account = await loadAccount();
  if (account) {
    /*
     * Автозамок ([ADR-0045]) перевіряється **до** відмикання: обгортка
     * пристрою відмикається без пароля, тож закрита годину тому вкладка
     * інакше відкрилася б сама, хоч людина просила замикати за хвилину.
     */
    if (lockDueAtStart()) {
      // Не `recallKeys` після забування: `account` уже прочитаний разом з
      // обгорткою, і вона відімкнула б архів із памʼяті — заміряно
      // перевіркою `lock.spec.ts`, що саме так і сталося.
      await forgetKeys();
      dataKeys = null;
    } else {
      dataKeys = await recallKeys(device, account).catch(() => null);
    }
    /*
     * Пара захоплення заводиться й тут, а не лише в онбордингу ([ADR-0011]).
     *
     * Пристрій, який памʼятає ключ даних, екрана замка не бачить взагалі —
     * тобто пари не завів би ніколи, аж поки людина не вийде й не ввійде
     * назад. Але саме перша замкнена сесія після виходу й потребує поля.
     * Те саме стосується всіх акаунтів, заведених до цієї задачі.
     *
     * Імпорт динамічний навмисно: модуль тягне криптографію, а вхідний чанк
     * лежить на кожному старті. Заміряно 2026-09-05 — статичний імпорт коштує
     * 335 байтів при запасі 441.
     *
     * Не чекаємо: старт не має спинятися заради того, що знадобиться аж
     * наступного разу.
     */
    if (dataKeys) {
      const dek = dataKeys.dek;
      void import("../platform/captureKeys.js")
        .then((m) => m.ensureCaptureKeys(dek))
        .catch(() => undefined);
    }
  }

  /**
   * Незавершене переведення архіву на чужий ключ — до всього іншого.
   *
   * Помилку тут **не** ковтаємо. Якщо третій крок не доробити, застосунок
   * відкриється зі старою обгорткою над базою, уже перекладеною на новий
   * ключ, — тобто з архівом, який мовчки не читається. Екран збою чесніший,
   * а перезапуск спробує ще раз.
   */
  const pendingRekey = await loadPendingRekey();
  if (pendingRekey) {
    // Модуль підвантажується лише тоді, коли є що доробляти: перехід
    // трапляється раз на життя пристрою, а старт буває щодня.
    const { finishRekey } = await import("../sync/rekey.js");
    await finishRekey(pendingRekey, await syncState.get(SYNC_KEYS.accountId));
  }

  setSyncToken(await loadSyncToken());
  serverDeviceId = await syncState.get(SYNC_KEYS.serverDeviceId);

  /**
   * Черга — декоратор, і це форма, а не прапорець.
   *
   * Аплаєр (щабель 4) триматиме **недекоровані** репозиторії, тож
   * дельта, застосована з мережі, не зможе поїхати назад авторові:
   * ехо-цикл просто немає чим написати. Прапорець «це від
   * синхронізації» дав би те саме лише доти, доки хтось його не забуде.
   */
  const plainEntries = new SqlEntryRepo(db, cipher);
  const plainHorizons = new SqlHorizonRepo(
    db,
    ids,
    clock,
    cipher,
    horizonIdWith(ids),
  );
  const outbox = new SqlOutbox(db, clock);
  const queueing: OutboxOptions = {
    db,
    outbox,
    // Кодек приходить ліниво: черга формату не знає взагалі, тож він
    // потрапляє у вкладку лише тоді, коли синхронізація ввімкнена.
    toPayload: {
      async entry(entry) {
        return (await payload()).entry(entry);
      },
      async item(item, kind, key) {
        return (await payload()).item(item, kind, key);
      },
      async person(person) {
        return (await payload()).person(person);
      },
      async personDate(date) {
        return (await payload()).personDate(date);
      },
      async personChannel(channel) {
        return (await payload()).personChannel(channel);
      },
    },
    enabled: () => syncToken !== null,
    onQueued: () => engine?.nudge(),
  };

  const started: Started = {
    db,
    search,
    profile: lazyRepo<ProfileRepo>(async () => {
      const { SqlProfileRepo } = await import("@beresta/db");
      return new SqlProfileRepo(db, clock, cipher);
    }),
    device,
    content: cipher,
    account,
    outbox,
    syncState,
    plain: { entries: plainEntries, horizons: plainHorizons },
    ctx: {
      entries: new OutboxEntryRepo(plainEntries, queueing),
      horizons: new OutboxHorizonRepo(plainHorizons, queueing),
      /*
       * Люди — з чергою, як записи й план.
       *
       * Декоратор будується **за лінивою межею**, разом із самим
       * репозиторієм: інакше клас черги для людей потрапив би в перший
       * кадр, який картки не показує. Для того, хто кличе, різниці
       * немає — усі методи порту асинхронні.
       */
      people: lazyRepo<PersonRepo>(async () => {
        const { OutboxPersonRepo, SqlPersonRepo } = await import("@beresta/db");
        return new OutboxPersonRepo(
          new SqlPersonRepo(db, ids, clock, cipher),
          queueing,
        );
      }),
      /*
       * Звʼязки згадок — не за лінивою межею, на відміну від людей.
       *
       * Їх перебудовує **кожне** збереження запису, тобто найгарячіший
       * шлях у застосунку. Динамічний імпорт тут коштував би затримки
       * рівно там, де людина пише, а сам клас дрібний: один UPDATE і
       * кілька INSERT.
       */
      links: lazyRepo<LinkPort>(async () => {
        const [{ SqlLinks }, { mentionsOf }] = await Promise.all([
          import("@beresta/db"),
          import("@beresta/ui/editor"),
        ]);
        return new SqlLinks(db, ids, clock, async (body) => mentionsOf(body));
      }),
      clock,
      ids,
      search,
    },
  };

  // Міграції, які не дійшли до кінця, доганяються тут.
  //
  // Без цього кроку перервана міграція не поновлювалася б ніколи:
  // онбординг більше не показується, а більше її ніхто не запускає.
  // Архів при цьому лишався б робочим — і саме тому пропажу було б
  // не видно роками, аж до першої синхронізації.
  //
  // Не чекаємо: обидві перевірки — це один запит, а сама робота йде
  // тлом, поки людина вже читає свій щоденник. Чекати не можна ще й
  // тому, що старт обмежений `STARTUP_DEADLINE_MS`.
  void catchUpMigrations(started).catch(() => undefined);

  startedRef = started;
  // Токен на місці — надсилання вирушає само, без жодного екрана.
  if (syncToken)
    void ensureSyncEngine()
      .then((e) => e.start())
      .catch(() => undefined);

  return started;
}

/**
 * Репозиторій за лінивою межею.
 *
 * Заміряно 2026-09-12: прямий `new SqlPersonRepo(...)` коштує **983 Б**
 * холодного старту, `new SqlLinks(...)` — ще **298 Б**. Обидва тягнуть у
 * перший кадр модулі, яких той кадр не торкається: картку людини
 * відкривають не щодня, а звʼязки перебудовуються лише при збереженні
 * запису. За ADR-0030 це пункт перший — не потрібне першому кадру, за межу.
 *
 * Проксі, а не двадцять обгорток: усі методи цих портів асинхронні, тож
 * підміна виклику на «дочекатися модуля, потім покликати» нічого не
 * змінює для того, хто кличе.
 */
function lazyRepo<T extends object>(load: () => Promise<T>): T {
  let pending: Promise<T> | null = null;
  const get = (): Promise<T> => (pending ??= load());

  return new Proxy({} as T, {
    get:
      (_target, method: string) =>
      async (...args: unknown[]) => {
        const real = (await get()) as unknown as Record<
          string,
          (...a: unknown[]) => unknown
        >;
        const fn = real[method];
        // Не `?.` — інакше друкарська помилка в назві методу тихо віддавала б
        // `undefined`, і екран показав би порожнечу замість падіння.
        if (typeof fn !== "function") {
          throw new TypeError(`порт не має методу ${method}`);
        }
        return fn.apply(real, args);
      },
  });
}

/** Те, що піднімається один раз. Стан акаунта поверх цього змінюється. */
interface Started {
  readonly db: WebDb;
  readonly search: SearchPort;
  readonly profile: ProfileRepo;
  readonly ctx: Ctx & PeopleCtx;
  readonly device: CipherPort;
  readonly content: ContentCipherPort;
  readonly account: LocalAccount | null;
  readonly outbox: OutboxPort;
  readonly syncState: SyncStatePort;
  /**
   * Репозиторії **без** черги — для аплаєра.
   *
   * Саме вони роблять ехо-цикл неможливим: дельта, застосована з
   * мережі, не потрапляє в чергу, тож поїхати назад авторові їй нема
   * через що. Прапорець «це від синхронізації» дав би те саме лише
   * доти, доки хтось його не забуде виставити.
   */
  readonly plain: {
    readonly entries: EntryRepo;
    readonly horizons: HorizonRepo;
  };
}

/**
 * Токен сесії сервера й рушій — модульні, як і ключ даних, і з тієї ж
 * причини: обидва зʼявляються після старту, а читає їх код, створений
 * при старті.
 *
 * Наявність токена **і є** ознакою «синхронізацію ввімкнено». Окремий
 * прапорець став би другим джерелом істини, і перше ж їх розходження
 * дало б застосунок, який вважає, що синхронізується, і мовчить.
 */
let syncToken: string | null = null;

/**
 * Хто хоче знати, що синхронізацію ввімкнули або вимкнули.
 *
 * Без цього `syncEnabled()` — звичайне читання модульної змінної, і React
 * не має причини перемалювати екран. Розділ ключа відновлення на цьому
 * ловився: людина вмикала синхронізацію в сусідньому розділі тих самих
 * налаштувань, а кнопка «замінити фразу» лишалася на місці й робила рівно
 * те, чого поруч написано, що воно не станеться.
 */
const syncListeners = new Set<() => void>();

export function subscribeSyncEnabled(listener: () => void): () => void {
  syncListeners.add(listener);
  return () => {
    syncListeners.delete(listener);
  };
}

/**
 * Токен сесії для тих, хто ходить на сервер повз синхронізацію.
 *
 * Поки такий був один — рушій синхронізації — токен лишався модульною
 * змінною, і читав його лише замикання в `HttpTransport`. Візитка ходить
 * тими самими дверима ([ADR-0037]), і копіювати те замикання вдруге
 * означало б завести другу відповідь на питання «а токен ще чинний».
 */
export function sessionToken(): string | null {
  return syncToken;
}

function setSyncToken(next: string | null): void {
  if (syncToken === next) return;
  syncToken = next;
  for (const listener of syncListeners) listener();
}
let serverDeviceId: string | null = null;
let engine: SyncEngine | null = null;

/** Кодек дельт — за потреби. Після першого разу це вже завантажений модуль. */
let payloadModule: Promise<ToPayload> | null = null;
function payload(): Promise<ToPayload> {
  payloadModule ??= import("../sync/payload.js").then((m) =>
    m.createToPayload(() => dataKeys?.dek ?? null),
  );
  return payloadModule;
}
/** Те, що піднялося. Потрібне рушієві, який будується вже після старту. */
let startedRef: Started | null = null;

/**
 * Рушій будується при першій потребі й лишається на весь час вкладки.
 *
 * Динамічний імпорт тут — не мікрооптимізація: транспорт, заливка й
 * кодек дельт разом важать саме стільки, щоб холодний старт перестав
 * вкладатися в бюджет. А потрібні вони лише тому, хто ввімкнув
 * надсилання.
 */
export async function ensureSyncEngine(): Promise<SyncEngine> {
  if (engine) return engine;
  const ready = startedRef;
  if (!ready) throw new Error("сховище ще не піднялося");

  const [
    { SyncEngine },
    { HttpTransport },
    { SqlApplier, SqlPersonRepo },
    { mergeDocs },
  ] = await Promise.all([
    import("../sync/engine.js"),
    import("../sync/transport.js"),
    import("@beresta/db"),
    // Yjs ліниво: він важить як пів застосунку й на холодному старті
    // заборонений перевіркою бюджету.
    import("@beresta/ui/editor"),
  ]);

  const applier = new SqlApplier({
    db: ready.db,
    entries: ready.plain.entries,
    horizons: ready.plain.horizons,
    /*
     * Власний, **недекорований** примірник, а не той, що в `ctx`.
     *
     * У `ctx` лежить `OutboxPersonRepo`, і чужа дельта, застосована
     * через нього, одразу лягла б назад у чергу — тобто поїхала б
     * назад авторові, той застосував би її знову, і так без кінця.
     * Будується тут, бо тут і лише тут він потрібен.
     */
    people: new SqlPersonRepo(
      ready.db,
      ready.ctx.ids,
      ready.ctx.clock,
      ready.content,
    ),
    ids: ready.ctx.ids,
    search: ready.search,
    // Той самий порт, що й у `ctx`: запис, який приїхав із другого
    // пристрою, має потрапити в історію на картці людини, а не лише
    // в пошук. Без цього вона мовчки неповна.
    links: ready.ctx.links,
    content: ready.content,
    mergeBody: mergeDocs,
  });

  engine ??= new SyncEngine(
    ready.db,
    ready.syncState,
    ready.outbox,
    ready.content,
    new HttpTransport(() => syncToken),
    applier,
    () => serverDeviceId,
    () => dataKeys?.dek ?? null,
  );
  return engine;
}

/** Синхронізацію щойно ввімкнули: токен є, можна вирушати. */
export async function holdSyncToken(
  token: string,
  deviceId: string,
): Promise<void> {
  setSyncToken(token);
  serverDeviceId = deviceId;
  (await ensureSyncEngine()).start();
}

/** Чи ввімкнена. Одне джерело — наявність токена. */
export function syncEnabled(): boolean {
  return syncToken !== null;
}

/**
 * Вимкнути. Токен зникає з пристрою, черга лишається: правки, зроблені
 * офлайн, не мають пропадати від того, що людина вимкнула надсилання.
 */
export async function disableSync(): Promise<void> {
  engine?.stop();
  setSyncToken(null);
  await forgetSyncToken();
}

function accountState(account: LocalAccount | null): AccountState {
  if (!account) return { status: "none" };
  return dataKeys
    ? { status: "unlocked", email: account.email, keys: dataKeys }
    : { status: "locked", email: account.email };
}

/**
 * Замкнути архів на цьому пристрої: сейф забуває ключ даних, памʼять теж.
 * Сам акаунт лишається — зайти можна парольною фразою.
 */
export async function lockDevice(): Promise<void> {
  await forgetKeys();
  dataKeys = null;
}

/** Прийняти щойно виведений ключ даних — після онбордингу або входу. */
export function holdKeys(keys: AccountKeys): void {
  dataKeys = keys;
}

/**
 * Спосіб перечитати стан акаунта ззовні React.
 *
 * Потрібен лише шву для перевірок: він заводить акаунт напряму й мусить
 * якось повідомити інтерфейс. Альтернатива — перезавантажувати сторінку —
 * коштувала дорого: перезавантаження посеред перевірки віддає захоплені
 * файли OPFS не одразу, і наступне читання падало з `NotReadableError`.
 * Ловилося лише на CI, тобто найгіршим способом.
 */
let refreshFromOutside: (() => Promise<void>) | null = null;

/**
 * Шов для перевірок продуктивності.
 *
 * Наповнити базу десятьма тисячами записів через інтерфейс неможливо — це
 * години кліків. Тому в режимі розробки контекст виставляється на `window`,
 * і перевірка Playwright наповнює базу напряму.
 *
 * Лише в DEV: у зібраному застосунку цього немає, і випадково лишити його
 * там не вийде — умову викидає збирач.
 */
/**
 * Чи є шов для перевірок.
 *
 * `DEV` — режим розробки. `VITE_E2E` вмикається лише командою, якою
 * Playwright збирає застосунок для власного прогону; у Dockerfile його
 * немає, тож у робочому образі ця гілка не існує — збирач її викидає.
 *
 * Навіщо друге: онбординг обовʼязковий і коштує секунду Argon2id.
 * Проходити його вісімдесят разів через інтерфейс — це п’ять зайвих
 * хвилин на кожному прогоні заради того, що перевіряється один раз
 * окремою добіркою.
 */
// ⚠️ Крапка, а не дужки. Vite підставляє значення лише для
// `import.meta.env.VITE_E2E`; запис через дужки лишається читанням
// властивості під час роботи, умова не згортається — і весь шов
// їде в робочу збірку. Виконати його ззовні не можна (`VITE_E2E`
// там `undefined`), але код лежить у вхідному чанку, тобто в
// бюджеті холодного старту. Заміряно 2026-10-03: −357 Б.
const SEAM = import.meta.env.DEV || import.meta.env.VITE_E2E === "1";

function exposeForTests(started: Started): void {
  if (!SEAM) return;
  (globalThis as { __beresta?: unknown }).__beresta = {
    ctx: started.ctx,
    search: started.search,
    profile: started.profile,
    db: started.db,
    device: started.device,
    /**
     * Завести акаунт зі свідомо слабкими параметрами Argon2id.
     *
     * Слабкими — бо тут не перевіряється вартість перебору, а перевіряється
     * все інше. Справжні параметри проходить окрема добірка, яка йде через
     * той самий інтерфейс, що й людина.
     *
     * Інтерфейс оновлюється тут же, без перезавантаження сторінки:
     * перезавантаження посеред перевірки віддає захоплені файли OPFS
     * не одразу, і наступне читання падало з `NotReadableError`.
     */
    /**
     * Простий текст → тіло запису тим самим кодувальником, що й редактор.
     *
     * Потрібне наповненню бази для замірів: воно створює десять тисяч
     * записів, і кожен має мати справжнє тіло, інакше індекс порожній,
     * а заміри — вигадані.
     */
    async makeBody(text: string) {
      const { bodyFromText } = await import("@beresta/ui/editor");
      return bodyFromText(text);
    },

    async bootstrapAccount(passphrase = "перевірочна парольна фраза") {
      // Ідемпотентно: у межах одного контексту перевірка може перейти
      // на інший екран і покликати це вдруге. Другий акаунт зробив би
      // попередні записи нечитними.
      if (dataKeys) return null;
      const { createAccount, newKdfParams } = await import("@beresta/core");
      const kdf = { ...newKdfParams(), memoryKiB: 8 * 1024, passes: 1 };
      const account = await createAccount(crypto.randomUUID(), passphrase, {
        kdf,
      });
      const { rememberKeys, saveAccount } =
        await import("../platform/account.js");
      await saveAccount(account.record, "perevirka@example.org");
      await rememberKeys(started.device, account.keys);
      // Те саме, що робить онбординг: копія 24 слів під ключем даних
      // ([ADR-0029]). Без цього кроку шов давав би стан, якого після
      // справжнього онбордингу не буває, — акаунт без збережених слів.
      // Підтвердження немає навмисно: шов його не питає, і рядок стану
      // має казати саме це.
      const { sealRecoveryPhrase } = await import("@beresta/core");
      const { saveSealedPhrase } = await import("../platform/recovery.js");
      await saveSealedPhrase(
        sealRecoveryPhrase(
          account.keys.dek,
          account.keys.accountId,
          account.recoveryPhrase,
        ),
        null,
      );
      holdKeys(account.keys);
      // І пара захоплення — теж те саме, що робить онбординг ([ADR-0011]).
      // Без неї шов давав би стан, якого після справжнього онбордингу не
      // буває: акаунт, на замку якого немає поля захоплення.
      const { ensureCaptureKeys } = await import("../platform/captureKeys.js");
      await ensureCaptureKeys(account.keys.dek);
      // Ті самі міграції, що й після справжнього онбордингу: акаунт
      // зʼявився посеред сесії, і без цього кроку горизонти лишилися б
      // із випадковими ідентифікаторами.
      await catchUpMigrations(started);
      await refreshFromOutside?.();
      return account.recoveryPhrase;
    },

    /**
     * Архів **без пароля** — той, що заводить реєстрація через Google
     * ([ADR-0040], варіант В).
     *
     * Окремий шов, а не прапорець до `bootstrapAccount`: різниця не в
     * одному полі, а в тому, що обгортки парольної немає взагалі, і саме
     * це відрізняє шлях, який перевіряється. Ключ-ескроу тут лишається
     * на пристрої лише на мить заведення — на сервер його кладе
     * реєстрація, якої в перевірці немає.
     */
    async bootstrapGoogleAccount() {
      if (dataKeys) return null;
      const { createEscrowAccount, newEscrowKey, sealRecoveryPhrase, toBase64, wipe } =
        await import("@beresta/core");
      const escrow = newEscrowKey();
      try {
        const made = createEscrowAccount(crypto.randomUUID(), escrow);
        /*
         * Те, що в житті дістається серверу при реєстрації, і те саме, що
         * він потім повертає на вході ([ADR-0040]). Перевірка грає сервера,
         * тож без цих значень вона не змогла б відповісти чесно — а
         * `signInWithGoogle` справді **розгортає** ними ключ даних, і
         * підробка «аби відповідь була 200» нічого б не довела.
         */
        const asServerSees = {
          accountId: made.record.accountId,
          kdf: {
            memoryKiB: made.record.kdf.memoryKiB,
            passes: made.record.kdf.passes,
            lanes: made.record.kdf.lanes,
            salt: toBase64(made.record.kdf.salt),
          },
          wraps: made.record.wraps.map((w) => ({ holder: w.holder, sealed: toBase64(w.sealed) })),
          google: { escrow: toBase64(escrow), sealed: toBase64(made.googleSealed) },
        };
        const { rememberKeys, saveAccount } = await import("../platform/account.js");
        await saveAccount(made.record, "perevirka@example.org");
        await rememberKeys(started.device, made.keys);
        const { saveSealedPhrase } = await import("../platform/recovery.js");
        await saveSealedPhrase(
          sealRecoveryPhrase(made.keys.dek, made.keys.accountId, made.recoveryPhrase),
          null,
        );
        holdKeys(made.keys);
        const { ensureCaptureKeys } = await import("../platform/captureKeys.js");
        await ensureCaptureKeys(made.keys.dek);
        await catchUpMigrations(started);
        await refreshFromOutside?.();
        return { recoveryPhrase: made.recoveryPhrase, asServerSees };
      } finally {
        wipe(escrow);
      }
    },
  };
}

export function DbProvider({ children }: { readonly children: ReactNode }) {
  const [started, setStarted] = useState<Started | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  // Росте, коли людина забирає сховище назад: це і є привід підняти його
  // ще раз, а не просто перемалювати екран.
  const [attempt, bumpAttempt] = useState(0);
  const [isParked, setParked] = useState(parked);
  const [account, setAccount] = useState<LocalAccount | null>(null);
  // Лічильник, а не сам стан: `dataKeys` живе поза React, і після входу
  // треба саме перерахувати похідний стан, а не покласти нове значення.
  const [revision, bump] = useState(0);

  useEffect(() => {
    notifyParked = () => {
      setParked(true);
      setStarted(null);
    };
    return () => {
      notifyParked = null;
    };
  }, []);

  useEffect(() => {
    if (isParked) return;
    let alive = true;
    // База не закривається при розмонтуванні: вона живе стільки ж, скільки
    // вкладка. Закрити її означало б віддати захоплені файли й не мати змоги
    // забрати назад, поки Chrome не звільнить дескриптори.
    startOnce().then(
      (ready) => {
        exposeForTests(ready);
        if (!alive) return;
        setAccount(ready.account);
        setStarted(ready);
      },
      (e: unknown) => {
        if (alive) setReason(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      alive = false;
    };
  }, [isParked, attempt]);

  const takeOver = useCallback(() => {
    takeOverStorage();
    setParked(false);
    setReason(null);
    bumpAttempt((n) => n + 1);
  }, []);

  const refreshAccount = useCallback(async () => {
    setAccount(await loadAccount());
    bump((n) => n + 1);
  }, []);

  // Автозамок за бажанням ([ADR-0045]): повернулися у вкладку пізніше,
  // ніж дозволено, — ключ забувається так само, як на «Вийти».
  useEffect(
    () =>
      watchAutoLock(() => {
        void lockDevice().then(refreshAccount);
      }),
    [refreshAccount],
  );

  useEffect(() => {
    refreshFromOutside = refreshAccount;
    return () => {
      refreshFromOutside = null;
    };
  }, [refreshAccount]);

  const state = useMemo<DbState>(() => {
    if (isParked) return { status: "parked", takeOver };
    if (reason !== null) return { status: "failed", reason };
    if (!started) return { status: "starting" };
    void revision;
    return {
      status: "ready",
      ctx: started.ctx,
      db: started.db,
      search: started.search,
      profile: started.profile,
      device: started.device,
      content: started.content,
      account: accountState(account),
      refreshAccount,
      syncEngine: ensureSyncEngine,
      syncState: started.syncState,
    };
  }, [started, reason, account, revision, refreshAccount, isParked, takeOver]);

  return <DbContext.Provider value={state}>{children}</DbContext.Provider>;
}
